import truncate from 'lodash.truncate';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import styled, { css } from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

import { Text, Typography } from '@americastestkitchen/mise';
import useAppContext from 'lib/hooks/useAppContext';
import { themeValue, withSiteTheme } from '../../../config/css';
import utils from '../../../lib/utils';
import FavoritesRibbon from '../../elements/FavoritesRibbon';
import DocumentBrowseCardContent from './components/DocumentBrowseCardContent';
import DocumentBrowseCardCta from './components/DocumentBrowseCardCta';
import DocumentBrowseCardImage from './components/DocumentBrowseCardImage';
import DocumentBrowseCardLabel from './components/DocumentBrowseCardLabel';
import DocumentBrowseCardLink from './components/DocumentBrowseCardLink';
import DocumentBrowseCardMetaTitle from './components/DocumentBrowseCardMetaTitle';
import DocumentBrowseCardPrice from './components/DocumentBrowseCardPrice';
import DocumentBrowseCardTitle from './components/DocumentBrowseCardTitle';

const DocumentBrowseCardTheme = {
  default: css`
    img {
      display: block;
    }

    &.horizontal {
      display: flex;
    }

    &.no-image {
      overflow: hidden;
    }
    &.no-image {
      background-color: ${themeValue('colors', 'white')};
    }
  `,

  parents: css`
    background-color: transparent;
    display: inline-block;
    height: 100%;
    margin-bottom: 0;
    max-width: 25rem;
    width: 25rem;

    img {
      height: 25rem;
      width: 25rem;
    }

    ${breakpoint('tablet')`
      max-width: 18rem;
      width: 18rem;

      img {
        height: auto;
      }
    `}

    ${breakpoint('desktop')`
      max-width: 21rem;
      width: 21rem;
    `}
  `,
};

const DocumentBrowseCardEl = styled.div.attrs((props) => ({
  className: [
    'document-browse-card',
    props.visualStyle,
    !props.hasImage ? 'no-image' : null,
  ].join(' '),
}))`
  ${withSiteTheme(DocumentBrowseCardTheme)}
`;

const DocumentBrowseCard = ({
  affiliateCode,
  ctaTitle = 'Buy the Winner',
  ctaUrl,
  currentPriceInPennies,
  description,
  favoritableId,
  includeCta,
  includeDescription = true,
  includeFavorite = true,
  includeSubtitle = true,
  isARelated = false,
  label,
  lazyImage = true,
  photo,
  previousPriceInPennies,
  queryParams = {},
  siteKey,
  subtitle,
  title,
  url,
  visualStyle = 'vertical',
}) => {
  const { domainSiteKey } = useAppContext();

  const { docSlug, docType } = utils.getDocInfoFromUrl(url) || {};
  const dataAttrs = {
    'data-title': title,
    'data-type': docType,
    'data-id': docSlug,
    'data-link': url,
  };
  const hasImage = (photo && (photo.id || photo.public_id)) || false;
  const isShop = siteKey === 'shop';
  const shouldRenderCta = includeCta && ctaUrl;
  const shouldRenderRibbon =
    includeFavorite && Boolean(favoritableId) && Boolean(siteKey);
  const titleLength = isARelated ? 30 : shouldRenderRibbon ? 45 : 54;
  return (
    <DocumentBrowseCardEl
      data-favorites-enabled={shouldRenderRibbon}
      hasImage={hasImage}
      visualStyle={visualStyle}
    >
      {hasImage ? (
        <DocumentBrowseCardImage
          favoritableId={favoritableId}
          dataAttrs={dataAttrs}
          includeFavorite={includeFavorite}
          isShop={isShop}
          label={label}
          lazyImage={lazyImage}
          photo={photo}
          siteKey={siteKey}
          title={title}
          url={url}
          visualStyle={visualStyle}
        />
      ) : null}
      <DocumentBrowseCardContent>
        {!hasImage && visualStyle !== 'quick-view-card' ? (
          <Fragment>
            {label ? (
              <DocumentBrowseCardLabel>{label}</DocumentBrowseCardLabel>
            ) : null}
            {shouldRenderRibbon ? (
              <FavoritesRibbon
                favoritableId={favoritableId}
                favoritableSiteKey={siteKey}
                title={title}
              />
            ) : null}
          </Fragment>
        ) : null}
        <DocumentBrowseCardLink
          {...dataAttrs}
          href={utils.checkRelativeLink(domainSiteKey, url)}
          target={isShop ? '_blank' : '_self'}
          title={title}
        >
          {includeSubtitle && subtitle ? (
            <DocumentBrowseCardMetaTitle>
              {typeof subtitle === 'string'
                ? truncate(subtitle, { length: 40 })
                : subtitle}
            </DocumentBrowseCardMetaTitle>
          ) : null}
          <Text as="span" variant="subheaderSmall">
            {truncate(title, { length: titleLength })}
          </Text>
        </DocumentBrowseCardLink>
        {includeDescription && description ? (
          <Typography type="dense" weight="normal" size="sm">
            {description}
          </Typography>
        ) : null}
        {currentPriceInPennies ? (
          <DocumentBrowseCardPrice
            currentPrice={currentPriceInPennies}
            previousPrice={previousPriceInPennies}
          />
        ) : shouldRenderCta ? (
          <DocumentBrowseCardCta
            affiliateCode={affiliateCode}
            ctaTitle={ctaTitle}
            ctaUrl={ctaUrl}
            dataAttrs={dataAttrs}
            queryParams={queryParams}
          />
        ) : null}
        {isShop && currentPriceInPennies && shouldRenderCta ? (
          <DocumentBrowseCardCta
            affiliateCode={affiliateCode}
            ctaTitle={ctaTitle}
            ctaUrl={ctaUrl}
            dataAttrs={dataAttrs}
            queryParams={queryParams}
          />
        ) : null}
      </DocumentBrowseCardContent>
      {visualStyle === 'quick-view-card' && shouldRenderRibbon ? (
        <FavoritesRibbon
          favoritableId={favoritableId}
          favoritableSiteKey={siteKey}
          title={title}
        />
      ) : null}
    </DocumentBrowseCardEl>
  );
};

DocumentBrowseCard.Title = DocumentBrowseCardTitle;

DocumentBrowseCard.propTypes = {
  /** Code for tag paramenter on amazon/affiliate links */
  affiliateCode: PropTypes.string,
  /** current price of item in pennies */
  currentPriceInPennies: PropTypes.number,
  /** text for action - usually something like 'buy now' */
  ctaTitle: PropTypes.string,
  /** URL for amazon/affiliate links */
  ctaUrl: PropTypes.string,
  /** Short supplementary body copy */
  description: PropTypes.string,
  /** <documemtType>_<slug/id> */
  favoritableId: PropTypes.string,
  /** Should there be a CTA link? */
  includeCta: PropTypes.bool,
  /** Should there be a description? */
  includeDescription: PropTypes.bool,
  /** Should there be a favorite ribbon? */
  includeFavorite: PropTypes.bool,
  /** Should there be a subtitle? */
  includeSubtitle: PropTypes.bool,
  /** True of false. Used to determine number of truncated characters */
  isARelated: PropTypes.bool,
  /* Optional label displayed at the top of the card */
  label: PropTypes.string,
  /* Should image lazy load? */
  lazyImage: PropTypes.bool,
  /** Object with cloudinary id */
  photo: PropTypes.object,
  /** previous price of item in pennies */
  previousPriceInPennies: PropTypes.number,
  /** Query strings parsed into object */
  queryParams: PropTypes.object,
  /** Site key for current site */
  siteKey: PropTypes.string.isRequired,
  /** Text or SVG located below image */
  subtitle: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /** Card title */
  title: PropTypes.string.isRequired,
  /** Detail page (or other) link */
  url: PropTypes.string,
  /** If 'horizontal', component will display side-by-side */
  visualStyle: PropTypes.string,
};

export default DocumentBrowseCard;
